import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, GeneralNotFound } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { getTask } from 'src/api/admin/tasks';
import { getUserTaskDocuments } from 'src/api/admin/documents';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/approvals/$approvalId')({
	loader: async ({ params }) => {
		const { userId, approvalId } = params;
		const task = await getTask(approvalId);

		let documents: Awaited<ReturnType<typeof getUserTaskDocuments>> = [];
		if (task && task.documentIds && task.documentIds.length > 0) {
			documents = await getUserTaskDocuments(userId, task.documentIds);
		}

		return { task, documents };
	},
	component: ApprovalDetailPage,
});

function ApprovalDetailPage() {
	const { userId, approvalId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { task: initialTask, documents: initialDocuments } = Route.useLoaderData();
	const { user: authenticatedUser } = useAuthenticatedUser();

	const { data: task, refetch: refetchTask } = useQuery({
		queryKey: ['task-detail', approvalId],
		queryFn: () => getTask(approvalId),
		initialData: initialTask,
	});

	const { data: documents, refetch: refetchDocuments } = useQuery({
		queryKey: ['task-documents', userId, approvalId],
		queryFn: () => getUserTaskDocuments(userId, task?.documentIds || []),
		initialData: initialDocuments,
		enabled: !!task,
	});

	const handleUpdate = () => {
		refetchTask();
		refetchDocuments();
		queryClient.invalidateQueries({ queryKey: ['user-approvals', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-roles-with-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-tasks', userId] });
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
	};

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this approval. Maybe it has been deleted?"
				action={{
					label: 'Return to approvals',
					href: `/users/${userId}/approvals`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const navigateToOverview = () => {
		queryClient.invalidateQueries({ queryKey: ['user-approvals', userId] });
		navigate({ to: '/users/$userId/approvals', params: { userId }, search: { page: undefined, sort: undefined } });
	};

	return (
		<TaskViewer
			userId={userId}
			task={task}
			documents={documents || []}
			onUpdate={handleUpdate}
			onClose={navigateToOverview}
			onDelete={navigateToOverview}
			onOpenDocument={(documentId) => window.open(`/users/${userId}/documents/${documentId}`)}
			permissions={{
				taskDelete: verifyPermission(PERMISSIONS.adminUserApprovalsDelete),
				taskDo: task.executePermission?.isAllowed,
			}}
		/>
	);
}
